import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import _ from "lodash";
import dayjs from "dayjs";
import { ProgressBar } from "primereact/progressbar";
import { useNavigate, useParams } from "react-router-dom";
import { assignedProgramRequest } from "../../../store/slices/selfSlice";
import CustomBreadCrumbs from "../../common/breadcrumbs";
import * as DisplayHelpers from "../../../helpers/DisplayHelper";
import "./learning-program-journey.scss";
import { Dialog } from "primereact/dialog";
import PreviewCertificate from "../certificates/preview";
import useMobileView from "../../common/hooks/useMobileView";
import { RouteConstants } from "../../../constants";
import { GetMilestonePreviewUrl } from "../../../helpers/GlobalHelper";
import ContentPreviewIFrame from "./ContentPreviewIFrame";

const LearningProgramJourney = ({
  labels,
  assignedProgramRequest,
  program,
  enterprise,
  token,
  action,
  error,
  requesting,
  menuCustomised,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const intl = useIntl();

  const { isMobile } = useMobileView();

  const [selectedObject, setSelectedObject] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [completeManually, setCompleteManually] = useState(false);
  const [selectedMilestones, setSelectedMilestones] = useState([]);

  const [showPreview, setShowPreview] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  useEffect(() => {
    if (action === "mark-as-complete-request" && !requesting && !error) {
      setPreviewUrl(null);
    }
  }, [action, error, requesting]);

  useEffect(() => {
    setCompleteManually(false);
    assignedProgramRequest({
      id: params.id,
    });
  }, [previewUrl]);

  const updateLinearity = (
    objects = [],
    isParentLinear = false,
    parentObjects = []
  ) => {
    let lastStatus = false;
    return objects.map((object, index) => {
      let response = { ...object, enabled: false };
      if (
        index === 0 ||
        isParentLinear === false ||
        object.status.toLowerCase() === "completed"
      ) {
        response.enabled = true;
      } else {
        if (object.aggregationType === "Or") {
          response.enabled = lastStatus;
        } else {
          for (var i = index - 1; i >= 0; i--) {
            if (parentObjects[i].status.toLowerCase() === "completed") {
              response.enabled = true;
              break;
            } else if (parentObjects[i].aggregationType === "And") {
              break;
            }
          }
        }
      }
      lastStatus = response.enabled;
      return response;
    });
  };

  const divideDateRange = useMemo(() => {
    // Convert startDate and endDate to dayjs objects
    if (!program?.startDate || !program?.endDate) return [];
    const start = dayjs(program?.startDate);
    const end = dayjs(program?.endDate);

    // Calculate the total duration in milliseconds
    const totalDuration = end.diff(start);

    // Calculate the interval for each part
    const count = program?.milestones?.length || 1;
    const interval = totalDuration / count;

    // Generate the intermediate dates
    const parts = [];
    for (let i = 1; i <= count; i++) {
      const partDate = start.add(interval * i, "millisecond");
      parts.push(partDate); // Convert to ISO string for readability
    }

    return parts;
  }, [program]);

  const milestoneEndDates = divideDateRange;

  let milestones = _.cloneDeep(program?.milestones);
  milestones = updateLinearity(
    milestones,
    program?.learningPathway?.isLinear,
    program?.milestones
  );

  milestones?.forEach((milestone, index) => {
    milestone.objects = updateLinearity(
      _.cloneDeep(milestone.objects),
      milestone?.milestone?.isLinear,
      milestone.objects
    );
  });

  if (!selectedObject && program?.id === params.id) {
    let milestoneId = null;
    let selectedObject = null;
    let milestoneIds = [];
    milestones?.forEach((milestone, index) => {
      const status = milestone.status.toLowerCase();
      if (milestone.enabled && status !== "completed" && !milestoneId) {
        milestoneId = milestone.id;
      }
      if (!milestoneId && index === milestones.length - 1) {
        milestoneId = milestone.id;
      }

      milestoneIds.push(milestone.id);

      if (milestoneId && !selectedObject && milestone.id === milestoneId) {
        selectedObject = milestone.objects.find(
          (obj) => obj.enabled && obj.status.toLowerCase() !== "completed"
        );
        if (!selectedObject) {
          selectedObject = milestone.objects[milestone.objects.length - 1];
        }
      }
    });
    setSelectedMilestones(milestoneIds);
    setSelectedObject(selectedObject);
  }

  const getStatusProps = (item) => {
    let status = item.status.toLowerCase();
    let color = "var(--orange-500)";
    if (status === "completed") {
      color = "var(--green-500)";
    } else if (item.endDate && dayjs() > dayjs(item.endDate)) {
      status = "overdue";
      color = "var(--red-500)";
    } else if (
      status === "inprogress" ||
      status === "started" ||
      status === "incomplete"
    ) {
      status = "inprogress";
      color = "#0EA5E9";
    } else if (!item.enabled) {
      status = "notstarted";
      color = "var(--gray-500)";
    }
    return { status, color };
  };

  return (
    <>
      <ContentPreviewIFrame
        completeManually={completeManually}
        object={selectedObject}
        previewUrl={previewUrl}
        setPreviewUrl={setPreviewUrl}
        setCompleteManually={setCompleteManually}
      />
      <div>
        <CustomBreadCrumbs
          labels={labels}
          menuCustomised={menuCustomised}
          navigate={navigate}
          editLabel={program?.learningPathway?.name}
        />
        <div className="edexlms-page-content">
          <div className="edexlms-page-header">
            <div className="edexlms-page-title">
              {program?.learningPathway?.name || ""}
            </div>
          </div>
          <div className="edexlms-page-body flex flex-column md:flex-row gap-4">
            <div className="edexlms-program-milestones">
              {milestones?.map((milestone, index) => {
                const totalTime = milestone.objects.reduce((acc, obj) => {
                  if (obj.aggregationType === "Or") {
                    return acc;
                  } else {
                    return acc + obj.learningObject.maxTimeInSecsAllowed;
                  }
                }, 0);
                const { status, color } = getStatusProps(milestone);
                return (
                  <div
                    className={`edexlms-program-milestone ${
                      selectedMilestones.includes(milestone.id) ? "active" : ""
                    } ${milestone.enabled ? "enabled" : "disabled"}`}
                    key={milestone.id}
                  >
                    <div className="edexlms-program-milestone-line">
                      <div
                        className="edexlms-program-milestone-line-circle"
                        style={{ borderColor: color }}
                      >
                        {index + 1}
                      </div>
                      {(milestoneEndDates?.[index] && (
                        <div className="edexlms-program-milestone-date">
                          <div
                            className="edexlms-program-milestone-month"
                            style={{
                              backgroundColor: milestone.enabled
                                ? color
                                : "#DADEE3",
                            }}
                          >
                            {dayjs(milestoneEndDates[index]).format("MMM")}
                          </div>
                          <div className="edexlms-program-milestone-day">
                            {dayjs(milestoneEndDates[index]).format("DD")}
                          </div>
                        </div>
                      )) ||
                        null}
                      <div
                        className="edexlms-connect-line"
                        style={{ backgroundColor: color }}
                      ></div>
                    </div>
                    <div>
                      <div
                        className={`edexlms-program-milestone-details ${
                          selectedMilestones.includes(milestone.id)
                            ? "active"
                            : ""
                        }`}
                      >
                        <div
                          className="edexlms-program-milestone-header"
                          onClick={() => {
                            const isAdded = selectedMilestones.includes(
                              milestone.id
                            );
                            if (!isAdded) {
                              setSelectedMilestones((prev) => [
                                ...prev,
                                milestone.id,
                              ]);
                            } else {
                              setSelectedMilestones((prev) =>
                                prev.filter((id) => id !== milestone.id)
                              );
                            }
                          }}
                        >
                          <div className="title">
                            {milestone.milestone.name}
                          </div>
                          <div className="expand-collapse-icon">
                            <i
                              className={`pi pi-chevron-${
                                selectedMilestones.includes(milestone.id)
                                  ? "up"
                                  : "down"
                              }`}
                              style={{ color: "#212121" }}
                            ></i>
                          </div>
                        </div>
                        <div className="details text-muted flex align-items-center">
                          <div>
                            {intl.formatMessage(
                              { id: "milestone_no" },
                              { index: index + 1 }
                            )}
                          </div>
                          <div className="circle-divider"></div>
                          <div className="milestone-time flex align-items-center gap-2">
                            <i
                              className="pi pi-clock"
                              style={{ fontSize: "0.9rem" }}
                            />
                            {DisplayHelpers.convertSeconds(totalTime)}
                          </div>
                        </div>
                        <div className="edexlms-program-milestone-status">
                          <ProgressBar
                            style={{ height: "0.5rem", width: "60%" }}
                            value={
                              milestone?.completionCount
                                ?.completionPercentage || 0
                            }
                            showValue={false}
                            color="var(--green-500)"
                          />
                          <div
                            style={{
                              backgroundColor: color,
                              color: "white",
                              padding: "0.2rem 0.5rem",
                              borderRadius: "0.5rem",
                              fontSize: "10px",
                            }}
                            className="text-sm"
                          >
                            {intl.formatMessage({ id: status }).toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <div className="edexlms-program-milestone-objects">
                        {milestone.objects.map((object, index) => {
                          const { status } = getStatusProps(object);
                          return (
                            <div
                              onClick={() => {
                                if (
                                  isMobile &&
                                  milestone.enabled &&
                                  object.enabled
                                ) {
                                  navigate(
                                    `${RouteConstants.APP_ROUTE_LEARNING_JOURNEY_PROGRAMS_MILESTONE_DETAILS.replace(
                                      ":id",
                                      params.id
                                    )
                                      .replace(":mid", milestone.id)
                                      .replace(":oid", object.id)}`
                                  );
                                } else {
                                  if (milestone.enabled && object.enabled)
                                    setSelectedObject(object);
                                }
                              }}
                              className={`edexlms-program-milestone-object ${
                                selectedObject?.id === object.id ? "active" : ""
                              } ${
                                milestone.enabled && object.enabled
                                  ? "enabled"
                                  : "disabled"
                              }`}
                              key={object.id}
                            >
                              <div className="object-title">
                                {object.learningObject.name}
                              </div>
                              <div className="object-duration">
                                {DisplayHelpers.convertSeconds(
                                  object.learningObject.maxTimeInSecsAllowed
                                )}
                              </div>
                              {(status === "completed" &&
                                object.learningObject.certificate && (
                                  <i
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedCertificate({
                                        ...object.learningObject.certificate,
                                        learningName:
                                          object.learningObject.name,
                                        dateofCompletion: new Date(
                                          object.completionDate
                                        ),
                                      });
                                      setShowPreview(true);
                                    }}
                                    className="pi pi-crown"
                                  ></i>
                                )) || (
                                <i
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                  className="pi pi-crown"
                                  style={{ opacity: 0 }}
                                ></i>
                              )}
                              <div className="object-status">
                                {(status === "completed" && (
                                  <i
                                    className="pi pi-check-circle"
                                    style={{ color: "var(--green-500)" }}
                                  />
                                )) || (
                                  <i className="pi pi-arrow-circle-right" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {(selectedObject && !isMobile && (
              <div className="edexlms-program-milestone-object-preview hidden md:block">
                <div className="edexlms-program-milestone-object-preview-header">
                  {selectedObject?.learningObject?.name || ""}
                </div>
                <div
                  className="edexlms-program-milestone-object-preview-baner"
                  style={{
                    cursor: selectedObject.learningObject.contentType
                      ? "pointer"
                      : "default",
                  }}
                  onClick={() => {
                    const payload = {
                      userLearningObjectId: selectedObject.id,
                      domain: enterprise.subDomain,
                      accessToken: token,
                      launchUrl: selectedObject.learningObject.launchPathUrl,
                    };

                    const previewURL = GetMilestonePreviewUrl(
                      selectedObject?.learningObject,
                      payload
                    );

                    if (previewURL) {
                      setPreviewUrl(previewURL);
                    }
                  }}
                >
                  <>
                    <img
                      style={{ opacity: 0.5 }}
                      src={`${new URL(
                        selectedObject.learningObject.bannerImage,
                        process.env.REACT_APP_URL
                      ).toString()}`}
                      alt={selectedObject?.learningObject?.name || ""}
                    />
                    {(selectedObject.learningObject.contentType && (
                      <i
                        className="pi pi-play-circle"
                        style={{
                          color: "var(--edexlms-primary-color)",
                          fontSize: 50,
                          position: "absolute",
                        }}
                      />
                    )) ||
                      null}
                  </>
                </div>
                {(!selectedObject?.learningObject?.contentType && (
                  <div
                    style={{
                      marginTop: "10px",
                      fontStyle: "italic",
                      fontSize: 12,
                    }}
                    className="flex gap-2 text-muted"
                  >
                    * Content not available
                  </div>
                )) ||
                  null}
              </div>
            )) ||
              null}
          </div>
        </div>
        <Dialog
          header={intl.formatMessage({ id: "preview_certificate" })}
          visible={showPreview}
          className="preview_cert"
          style={{
            height: isMobile ? "480px" : "700px",
            width: isMobile ? "380px" : "600px",
            minHeight: isMobile ? "480px" : "700px",
            minWidth: isMobile ? "380px" : "600px",
          }}
          onHide={() => setShowPreview(false)}
        >
          <PreviewCertificate
            url={selectedCertificate?.url}
            orientation={selectedCertificate?.orientation}
            isActual={true}
            learningName={selectedCertificate?.learningName}
            dateofCompletion={selectedCertificate?.dateofCompletion}
          />
        </Dialog>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  program: state.self.program,
  enterprise: state.global.enterprise,
  token: state.auth.session.accessToken,
  action: state.self.action,
  error: state.self.error,
  requesting: state.self.requesting,
});

const mapDisptachToProps = (dispatch) => ({
  assignedProgramRequest: (data) => dispatch(assignedProgramRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDisptachToProps
)(LearningProgramJourney);
